<template>
  <div class="interrupt-block">
    <div v-for="interrupt in chunk.data" :key="interrupt.id">
      <!-- Submitted: done pill matching ToolCallBlockSSE done state. Answered approvals usually show as a
           step of the reply's tool calls block instead (see utils/message.js isStepGroup). -->
      <div v-if="interrupt.resolvedDecision" class="interrupt-block__pill">
        <span
          class="chunk-status-icon"
          :class="
            interrupt.resolvedDecision === APPROVAL_DECISION.REJECT
              ? 'chunk-status-icon--error'
              : 'chunk-status-icon--done'
          "
        >
          <BbIcon
            size="small"
            :name="interrupt.resolvedDecision === APPROVAL_DECISION.REJECT ? 'x-small' : 'checkmark-small'"
          />
        </span>
        <span class="interrupt-block__pill-label">
          {{
            interrupt.value.type === INTERRUPT_REQUEST_TYPE.IMAGE_STYLE_CHOICES
              ? t('conversation.imageParams.imageGeneration')
              : formatKey(interrupt.value.action_requests?.[0]?.name ?? '')
          }}
          <span class="interrupt-block__pill-decision">· {{ interrupt.resolvedDecision }}</span>
        </span>
      </div>

      <!-- Pending approval: its own card -->
      <HitlReviewInterrupt
        v-else-if="interrupt.value.type !== INTERRUPT_REQUEST_TYPE.IMAGE_STYLE_CHOICES"
        :interrupt="interrupt"
        @submitted="markSubmitted(interrupt.id, $event)"
      />

      <!-- Pending image style choice: expansion panel matching ToolCallBlockSSE open state -->
      <v-expansion-panels v-else flat model-value="0">
        <v-expansion-panel value="0">
          <v-expansion-panel-title>
            <div class="interrupt-block__title">
              <span class="interrupt-block__title-label"> Arcane needs your approval before it can continue: </span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <ImageStyleChoiceInterrupt @submitted="markSubmitted(interrupt.id, APPROVAL_DECISION.SUBMITTED)" />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import { APPROVAL_DECISION, INTERRUPT_REQUEST_TYPE } from '~/constants/conversationModels';
import { ResolveInterrupt } from '~/constants/mutations';
import { formatKey } from '~/utils/format';

import ImageStyleChoiceInterrupt from './ImageStyleChoiceInterrupt.vue';
import HitlReviewInterrupt from './HitlReviewInterrupt.vue';

export default {
  name: 'InterruptBlockSSE',
  components: { ImageStyleChoiceInterrupt, HitlReviewInterrupt },
  props: {
    chunk: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { t } = useI18n();
    const store = useStore();

    // The answer goes into the store as the interrupt's `resolvedDecision`, the same field a reloaded
    // conversation has, so the reply's tool calls block can show it as a step.
    const markSubmitted = (interruptId, decision) => {
      store.commit(`conversationStore/${ResolveInterrupt}`, { interruptId, decision });
    };

    return {
      t,
      formatKey,
      markSubmitted,
      APPROVAL_DECISION,
      INTERRUPT_REQUEST_TYPE,
    };
  },
};
</script>

<style scoped lang="scss">
.interrupt-block {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-2;
}

// Done pill — mirrors ToolCallBlockSSE --done state
.interrupt-block__pill {
  display: inline-flex;
  align-items: center;
  gap: $bb-spacing-2;
  background-color: $bb-neutral-6;
  border-radius: 100px;
  padding: $bb-spacing-2;
  width: fit-content;
}

.interrupt-block__pill-label {
  font-size: 12px;
  font-weight: 500;
  color: $bb-neutral-1;
  line-height: 16px;
}

.interrupt-block__pill-decision {
  color: $bb-neutral-3;
  font-weight: 400;
}

// Shared icon styles (mirrors ToolCallTitle / ToolStepRow)
.chunk-status-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 16px;
  height: 16px;
}
.chunk-status-icon--done {
  background-color: #000;
  color: $bb-neutral-0 !important;
}
.chunk-status-icon--error {
  background: #fee2e2;
  color: $bb-interface-5 !important;
}

// Expansion panel — mirrors ToolCallBlockSSE open state
:deep(.v-expansion-panel) {
  background-color: $bb-neutral-6;
  border-radius: 16px !important;

  .v-expansion-panel__shadow {
    box-shadow: none;
  }

  .v-expansion-panel-title {
    min-height: unset;
    padding: $bb-spacing-2 $bb-spacing-3;

    .v-expansion-panel-title__overlay {
      background-color: $bb-neutral-6;
    }
  }

  .v-expansion-panel-text .v-expansion-panel-text__wrapper {
    padding: $bb-spacing-2 $bb-spacing-6 $bb-spacing-4 !important;
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-3;
  }
}

.interrupt-block__title {
  display: flex;
  align-items: center;
  gap: $bb-spacing-2;
  flex: 1 1 auto;
}

.interrupt-block__title-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
}
</style>
